import React, { useEffect, useRef, useState } from 'react';
import { parseAddresses, addrFull, fmtSize, quoteText, fmtDate } from '../../data/mailClient';

// Compose / reply / forward. Plain-text body (the server wraps it as
// text/plain; the reader renders it with preserved whitespace). Attachments
// upload to the JMAP blob endpoint through the proxy first; the final send
// goes through POST /mail/send, which applies the instance caps.
//
// props: client, draftsId, limits, signature, initial = { mode, email } |
//        { to, subject, text }, onClose, onSent, toast
export default function Compose({ client, draftsId, limits = {}, signature = '', initial = {}, onClose, onSent, toast }) {
  const seed = seedFrom(initial, client, signature);
  const [to, setTo] = useState(seed.to);
  const [cc, setCc] = useState(seed.cc);
  const [bcc, setBcc] = useState(seed.bcc);
  const [showCc, setShowCc] = useState(!!(seed.cc || seed.bcc));
  const [subject, setSubject] = useState(seed.subject);
  const [text, setText] = useState(seed.text);
  const [attachments, setAttachments] = useState(seed.attachments);
  const [uploading, setUploading] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [draftId, setDraftId] = useState(initial.mode === 'draft' ? initial.email?.id : null);
  const [minimized, setMinimized] = useState(false);
  const bodyRef = useRef(null);
  const dirty = useRef(false);

  const [drop, setDrop] = useState(false);

  // Drafts autosave: every 30 s while dirty (and on close).
  useEffect(() => {
    if (!draftsId) return undefined;
    const id = setInterval(() => { if (dirty.current && (to || subject || attachments.length || text !== seed.text)) saveDraft(); }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftsId, to, cc, bcc, subject, text, attachments, draftId]);

  useEffect(() => {
    const el = initial.mode && initial.mode !== 'draft' ? bodyRef.current : null;
    el?.focus();
    el?.setSelectionRange(0, 0);
  }, [initial.mode]);

  const recipients = () => ({ to: parseAddresses(to), cc: parseAddresses(cc), bcc: parseAddresses(bcc) });
  const totalRcpts = () => { const r = recipients(); return r.to.length + r.cc.length + r.bcc.length; };
  const attBytes = attachments.reduce((n, a) => n + (a.size || 0), 0);
  const attMax = (Number(limits.attachmentMb) || 25) * 1048576;

  const addFiles = async (files) => {
    for (const f of files) {
      if (attBytes + f.size > attMax) { setErr(`attachments exceed ${limits.attachmentMb || 25} MB`); continue; }
      setUploading((n) => n + 1);
      try {
        const a = await client.upload(f);
        setAttachments((xs) => [...xs, a]);
        dirty.current = true;
      } catch (e) {
        setErr(e.message);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const saveDraft = async () => {
    if (!draftsId) return;
    try {
      const r = recipients();
      const id = await client.saveDraft(draftsId, { ...r, subject, text, inReplyTo: seed.inReplyTo, references: seed.references, attachments, replaceId: draftId });
      setDraftId(id);
      dirty.current = false;
      toast?.('Draft saved');
    } catch (e) {
      setErr(e.message);
    }
  };

  const send = async () => {
    setErr('');
    const r = recipients();
    if (!r.to.length && !r.cc.length && !r.bcc.length) { setErr('add at least one recipient'); return; }
    const bad = [to, cc, bcc].join(',').split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean).filter((s) => !/^.*<[^@\s]+@[^@\s]+\.[^@\s]+>$|^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s));
    if (bad.length) { setErr(`check these addresses: ${bad.join(', ')}`); return; }
    if (totalRcpts() > (Number(limits.recipientsPerMessage) || 50)) { setErr(`max ${limits.recipientsPerMessage} recipients per message`); return; }
    if (uploading) { setErr('attachments are still uploading'); return; }
    setBusy(true);
    try {
      await client.send({ ...r, subject, text, attachments, inReplyTo: seed.inReplyTo, references: seed.references, draftId });
      dirty.current = false;
      onSent?.();
      onClose();
    } catch (e) {
      setErr(e.outcome === 'paused' ? `Sending is paused on this domain: ${e.message}` : e.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (dirty.current && (to || subject || text !== seed.text) && draftsId) saveDraft();
    onClose();
  };

  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); send(); }
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  return (
    <div className={`ml-compose ${minimized ? 'is-min' : ''} ${drop ? 'is-drop' : ''}`} role="dialog" aria-label="New message" onKeyDown={onKey}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDrop(true); } }}
      onDragLeave={() => setDrop(false)}
      onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); setDrop(false); addFiles([...e.dataTransfer.files]); } }}>
      <div className="ml-compose-head">
        <strong>{seed.title}</strong>
        <span className="ml-compose-head-actions">
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setMinimized((m) => !m)} aria-label={minimized ? 'Expand' : 'Minimize'}>{minimized ? '▴' : '▾'}</button>
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={close} aria-label="Close">✕</button>
        </span>
      </div>
      {!minimized && (
        <>
          <label className="ml-field">
            <span>To</span>
            <input className="cs-input" value={to} onChange={(e) => { setTo(e.target.value); dirty.current = true; }} placeholder="name@example.com, …" autoFocus={!initial.mode || initial.mode === 'forward'} />
            {!showCc && <button type="button" className="ml-linkbtn" onClick={() => setShowCc(true)}>Cc/Bcc</button>}
          </label>
          {showCc && (
            <>
              <label className="ml-field"><span>Cc</span><input className="cs-input" value={cc} onChange={(e) => { setCc(e.target.value); dirty.current = true; }} /></label>
              <label className="ml-field"><span>Bcc</span><input className="cs-input" value={bcc} onChange={(e) => { setBcc(e.target.value); dirty.current = true; }} /></label>
            </>
          )}
          <label className="ml-field"><span>Subject</span><input className="cs-input" value={subject} onChange={(e) => { setSubject(e.target.value); dirty.current = true; }} /></label>
          <textarea ref={bodyRef} className="cs-input ml-compose-body" value={text} onChange={(e) => { setText(e.target.value); dirty.current = true; }} spellCheck placeholder="Write your message…" />
          {attachments.length > 0 && (
            <ul className="ml-att-list">
              {attachments.map((a) => (
                <li key={a.blobId} className="ml-att">
                  <span className="ml-att-name">{a.name}</span>
                  <span className="cs-help">{fmtSize(a.size)}</span>
                  <button type="button" className="ml-linkbtn" onClick={() => { setAttachments((xs) => xs.filter((x) => x.blobId !== a.blobId)); dirty.current = true; }}>remove</button>
                </li>
              ))}
            </ul>
          )}
          {err && <p className="ml-err" role="alert">{err}</p>}
          <div className="ml-compose-foot">
            <button type="button" className="cs-btn cs-btn-primary" onClick={send} disabled={busy || uploading > 0} data-agent-noclick>{busy ? 'Sending…' : 'Send'}</button>
            <label className="cs-btn cs-btn-ghost cs-btn-sm ml-attach-btn">
              {uploading ? `Uploading ${uploading}…` : 'Attach'}
              <input type="file" multiple hidden onChange={(e) => { addFiles([...e.target.files]); e.target.value = ''; }} />
            </label>
            {draftsId && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={saveDraft}>Save draft</button>}
            <span className="cs-help ml-compose-hint">{totalRcpts()}/{limits.recipientsPerMessage || 50} recipients · {fmtSize(attBytes)} of {limits.attachmentMb || 25} MB · ⌘↵ to send</span>
          </div>
        </>
      )}
    </div>
  );
}

function seedFrom(initial, client, signature) {
  const sig = signature ? `\n\n-- \n${signature}` : '';
  const base = { title: 'New message', to: '', cc: '', bcc: '', subject: '', text: `\n${sig}`.replace(/^\n(?=\n)/, ''), attachments: [], inReplyTo: null, references: null };
  const e = initial.email;
  if (!e) return { ...base, to: initial.to || '', subject: initial.subject || '', text: initial.text != null ? `${initial.text}${sig}` : base.text };
  const me = String(client.address).toLowerCase();
  const refs = [...(e.references || []), ...(e.messageId || [])];
  const header = `On ${fmtDate(e.receivedAt, { long: true })}, ${addrFull((e.from || [])[0])} wrote:`;
  if (initial.mode === 'draft') {
    return { ...base, title: 'Draft', to: (e.to || []).map(addrFull).join(', '), cc: (e.cc || []).map(addrFull).join(', '), bcc: (e.bcc || []).map(addrFull).join(', '), subject: e.subject || '', text: (e.bodyValues?.[e.textBody?.[0]?.partId]?.value) || '', attachments: (e.attachments || []).map((a) => ({ blobId: a.blobId, name: a.name, type: a.type, size: a.size })), inReplyTo: e.inReplyTo?.[0] || null, references: e.references || null };
  }
  if (initial.mode === 'forward') {
    return { ...base, title: 'Forward', subject: /^fwd?:/i.test(e.subject || '') ? e.subject : `Fwd: ${e.subject || ''}`, text: `${sig}\n\n---------- Forwarded message ----------\nFrom: ${(e.from || []).map(addrFull).join(', ')}\nDate: ${fmtDate(e.receivedAt, { long: true })}\nSubject: ${e.subject || ''}\nTo: ${(e.to || []).map(addrFull).join(', ')}\n\n${quoteText(e).replace(/^> ?/gm, '')}`, attachments: (e.attachments || []).map((a) => ({ blobId: a.blobId, name: a.name, type: a.type, size: a.size })), references: refs };
  }
  const replyTo = (e.replyTo?.length ? e.replyTo : e.from) || [];
  const others = initial.mode === 'replyAll' ? [...(e.to || []), ...(e.cc || [])].filter((a) => String(a.email).toLowerCase() !== me && !replyTo.some((r) => r.email === a.email)) : [];
  return {
    ...base,
    title: initial.mode === 'replyAll' ? 'Reply all' : 'Reply',
    to: replyTo.map(addrFull).join(', '),
    cc: others.map(addrFull).join(', '),
    subject: /^re:/i.test(e.subject || '') ? e.subject : `Re: ${e.subject || ''}`,
    text: `${sig}\n\n${header}\n${quoteText(e)}`,
    inReplyTo: e.messageId?.[0] || null,
    references: refs,
  };
}
